import { Injectable } from '@angular/core';
import { combineQueries } from '@datorama/akita';
import { ImpexChildQuery } from '@y42/impex';
import { JobStatus, Y42FeatureType } from '@y42/json-schema';
import { isNotNullish } from '@y42/util/nullish';
import { filter, map, Observable, shareReplay } from 'rxjs';
import { ExportTriggerJobUIModel } from '../export-trigger-job';
import { ExportTriggerJobQuery } from '../export-trigger-job/export-trigger-job.query';
import { ExportStoreQuery } from '../export/export.store-query';
import { ExportTriggerState, ExportTriggerUIModel } from './export-trigger.model';
import { ExportTriggerStore } from './export-trigger.store';

@Injectable({ providedIn: 'root' })
export class ExportTriggerQuery extends ImpexChildQuery<ExportTriggerState, ExportTriggerUIModel> {
  constructor(
    protected readonly store: ExportTriggerStore,
    protected readonly parentQuery: ExportStoreQuery,
    private readonly childQuery: ExportTriggerJobQuery,
  ) {
    super({
      store,
      parentQuery,
      toUIModel: (storeModel) => {
        const jobs = (storeModel?.$children || [])
          .map((jobId) => this.childQuery.getUIEntity(jobId))
          .filter(isNotNullish)
          // sort newest jobs first
          .sort((a, b) => {
            if (!b.created_at && !a.created_at) {
              return 0;
            }

            if (!b.created_at || !a.created_at) {
              return (b.created_at ? 1 : 0) - (a.created_at ? 1 : 0);
            }

            return new Date(b.created_at).valueOf() - new Date(a.created_at).valueOf();
          });

        const latest_job: ExportTriggerJobUIModel | undefined = jobs[0];
        const latest_valid_job =
          latest_job?.status === JobStatus.Ready ? latest_job : jobs.find(({ status }) => status === JobStatus.Ready);

        return {
          featureType: Y42FeatureType.Export,
          ...storeModel,
          export: this.parentQuery.getEntity(storeModel.$parent)!,
          jobs: storeModel.$children.map((id) => this.childQuery.getUIEntity(id)).filter(isNotNullish),
          latest_job: latest_job ? this.childQuery.toUIModel(latest_job) : undefined,
          latest_valid_job: latest_valid_job ? this.childQuery.toUIModel(latest_valid_job) : undefined,
        };
      },
    });
  }

  readonly activeItems$: Observable<ExportTriggerUIModel[]> = combineQueries([
    this.parentQuery.selectActive(),
    this.selectAll(),
    this.childQuery.selectAll(),
  ]).pipe(
    map(([activeParent]) => activeParent?.$children.map((child) => this.getUIEntity(child)).filter(isNotNullish) || []),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly items$: Observable<ExportTriggerUIModel[]> = combineQueries([
    this.selectAll(),
    this.childQuery.selectAll(),
  ]).pipe(
    map(([tables]) => tables.map(this.toUIModel)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly active$: Observable<ExportTriggerUIModel> = combineQueries([
    this.selectActive().pipe(filter(Boolean)),
    this.childQuery.selectAll(),
  ]).pipe(map(([active]) => this.toUIModel(active)));
}
